interface BrandGroup extends HTMLElement {
	dataset: DOMStringMap & { brandGroup: string };
}

interface FilterButton extends HTMLElement {
	dataset: DOMStringMap & { filter: string };
}

const BREAKPOINTS = {
	mobile: 575,
	tablet: 1099,
} as const;

const DESKTOP_STICKY_OFFSET: number = 32;
const OVERLAY_SCROLL_THRESHOLD: number = 50;

const COLUMNS = {
	mobile: 2,
	tablet: 3,
	desktop: 6,
} as const;

const GAPS = {
	mobile: 20,
	tablet: 30,
	desktop: 40,
} as const;

/**
 * Keeps the sticky filter bar positioned below the fixed header.
 * Tracks header hidden/visible state and adjusts `top` with a smooth transition.
 */
function initStickyHeader(): void {
	const stickyEl = document.querySelector<HTMLElement>('.all-brands__sticky');
	const overlay = document.querySelector<HTMLElement>('[data-gradient-overlay]');
	if (!stickyEl) return;

	const header = document.querySelector<HTMLElement>('.shopify-section-header');
	const announcement = document.querySelector<HTMLElement>('.section-announcement');
	// null = never evaluated — forces first tick to always set top regardless of header state
	let lastHidden: boolean | null = null;

	const updateOverlay = (): void => {
		if (!overlay) return;
		if (window.scrollY < OVERLAY_SCROLL_THRESHOLD) {
			overlay.style.height = '0';
			return;
		}
		const headerH: number = header?.offsetHeight ?? 0;
		const annH: number = announcement?.offsetHeight ?? 0;
		const stickyH: number = stickyEl.offsetHeight;
		overlay.style.height = `${headerH + annH + stickyH}px`;
	};

	const tick = (): void => {
		if (!header) return;

		const isHidden: boolean = header.classList.contains('shopify-section-header-hidden');

		if (isHidden !== lastHidden) {
			lastHidden = isHidden;

			if (isHidden) {
				const annBottom: number = announcement
					? announcement.offsetHeight + parseFloat(getComputedStyle(announcement).top || '0')
					: 0;
				stickyEl.style.top = `${annBottom + 22}px`;
			} else {
				const isDesktop: boolean = window.innerWidth > BREAKPOINTS.tablet;
				const headerH: number = header.offsetHeight;
				const annH: number = announcement?.offsetHeight ?? 0;
				stickyEl.style.top = `${headerH + annH + (isDesktop ? DESKTOP_STICKY_OFFSET : 0)}px`;
			}

			updateOverlay();
		}

		requestAnimationFrame(tick);
	};

	window.addEventListener('scroll', updateOverlay, { passive: true });
	updateOverlay();
	requestAnimationFrame(tick);
}

/**
 * Returns the number of columns based on viewport width.
 */
function getColCount(): number {
	const w = window.innerWidth;
	if (w <= BREAKPOINTS.mobile) return COLUMNS.mobile;
	if (w <= BREAKPOINTS.tablet) return COLUMNS.tablet;
	return COLUMNS.desktop;
}

/**
 * Returns the column gap in pixels based on viewport width.
 */
function getGap(): number {
	const w = window.innerWidth;
	if (w <= BREAKPOINTS.mobile) return GAPS.mobile;
	if (w <= BREAKPOINTS.tablet) return GAPS.tablet;
	return GAPS.desktop;
}

/**
 * Masonry layout: positions brand groups absolutely in a left-to-right
 * order with no wasted vertical space.
 */
function layoutMasonry(grid: HTMLElement): void {
	const cols = getColCount();
	const gap = getGap();
	const gridWidth = grid.offsetWidth;
	const colWidth = (gridWidth - gap * (cols - 1)) / cols;

	const visible = Array.from(
		grid.querySelectorAll<BrandGroup>('[data-brand-group]')
	).filter((g) => !g.hidden);

	// Reset to measure natural heights
	visible.forEach((g) => {
		g.style.position = 'absolute';
		g.style.width = `${colWidth}px`;
		g.style.left = '0px';
		g.style.top = '0px';
		g.style.visibility = 'hidden';
	});

	// Track column heights
	const colHeights: number[] = new Array(cols).fill(0);

	visible.forEach((g, i) => {
		// Strict left-to-right: item i goes into column i % cols
		const col = i % cols;
		const x = col * (colWidth + gap);
		const y = colHeights[col];

		g.style.left = `${x}px`;
		g.style.top = `${y}px`;
		g.style.width = `${colWidth}px`;
		g.style.visibility = '';

		colHeights[col] += g.offsetHeight;
	});

	// Set container height to tallest column
	grid.style.height = `${Math.max(...colHeights)}px`;
}

/**
 * Handles A-Z filter button clicks.
 * Shows/hides brand groups based on the selected letter.
 */
function initFilter(
	filter: HTMLElement,
	searchInput: HTMLInputElement | null,
	grid: HTMLElement
): void {
	filter.addEventListener('click', (e: Event) => {
		const target = e.target as HTMLElement;
		const btn = target.closest<FilterButton>('[data-filter]');
		if (!btn) return;

		if (searchInput) searchInput.value = '';

		filter
			.querySelectorAll<HTMLElement>('.all-brands__filter-btn')
			.forEach((b) => b.classList.remove('active'));
		btn.classList.add('active');

		const value = btn.dataset.filter;
		const groups = document.querySelectorAll<BrandGroup>('[data-brand-group]');

		groups.forEach((group) => {
			group
				.querySelectorAll<HTMLElement>('.all-brands__link')
				.forEach((link) => {
					const li = link.closest<HTMLElement>('li');
					if (li) li.hidden = false;
				});

			if (value === 'all') {
				group.hidden = group.dataset.brandGroup === '#';
			} else {
				group.hidden = group.dataset.brandGroup !== value;
			}
		});

		layoutMasonry(grid);

		window.scrollTo({ top: 0, behavior: 'smooth' });
	});
}

/**
 * Toggles reset button visibility based on input value.
 */
function updateResetButton(
	searchInput: HTMLInputElement,
	resetBtn: HTMLElement | null
): void {
	if (!resetBtn) return;
	const hasValue: boolean = searchInput.value.trim().length > 0;
	resetBtn.hidden = !hasValue;
}

/**
 * Runs the brand search/filter logic for the current query.
 */
function runSearch(
	query: string,
	filter: HTMLElement,
	grid: HTMLElement
): void {
	const groups: NodeListOf<BrandGroup> = document.querySelectorAll<BrandGroup>('[data-brand-group]');

	if (!query) {
		const activeBtn: HTMLElement | null =
			filter.querySelector<HTMLElement>('.all-brands__filter-btn.active') ??
			filter.querySelector<HTMLElement>('[data-filter="all"]');
		if (activeBtn) activeBtn.click();
		return;
	}

	filter
		.querySelectorAll<HTMLElement>('.all-brands__filter-btn')
		.forEach((b: HTMLElement) => b.classList.remove('active'));

	groups.forEach((group: BrandGroup) => {
		const heading: HTMLElement | null = group.querySelector<HTMLElement>('.all-brands__letter');
		const headingMatch: boolean =
			heading?.textContent?.trim().toLowerCase().startsWith(query) ?? false;

		const links: NodeListOf<HTMLAnchorElement> = group.querySelectorAll<HTMLAnchorElement>('.all-brands__link');
		let hasVisibleLink: boolean = false;

		links.forEach((link: HTMLAnchorElement) => {
			const linkMatch: boolean = (link.textContent ?? '').toLowerCase().includes(query);
			const li: HTMLElement | null = link.closest<HTMLElement>('li');
			if (li) li.hidden = !linkMatch;
			if (linkMatch) hasVisibleLink = true;
		});

		group.hidden = !(headingMatch || hasVisibleLink);

		if (headingMatch) {
			links.forEach((link: HTMLAnchorElement) => {
				const li: HTMLElement | null = link.closest<HTMLElement>('li');
				if (li) li.hidden = false;
			});
		}
	});

	layoutMasonry(grid);
}

/**
 * Handles search input — filters by letter heading and vendor name.
 * Includes reset button show/hide and clear behavior matching header search.
 */
function initSearch(
	searchInput: HTMLInputElement,
	filter: HTMLElement,
	grid: HTMLElement
): void {
	const resetBtn: HTMLElement | null = document.querySelector<HTMLElement>('[data-brands-search-reset]');

	searchInput.addEventListener('input', () => {
		updateResetButton(searchInput, resetBtn);
		const query: string = searchInput.value.trim().toLowerCase();
		runSearch(query, filter, grid);
	});

	if (resetBtn) {
		resetBtn.addEventListener('mousedown', (e: Event) => {
			e.preventDefault();
			searchInput.value = '';
			resetBtn.hidden = true;
			searchInput.focus();
			runSearch('', filter, grid);
		});
	}
}

/**
 * Main entry point — initializes all brand page features.
 */
export default function allBrands(): void {
	initStickyHeader();

	const grid = document.querySelector<HTMLElement>('[data-brands-grid]');
	const filter = document.querySelector<HTMLElement>('[data-brands-filter]');
	const searchInput = document.querySelector<HTMLInputElement>('[data-brands-search]');

	if (!grid) return;

	layoutMasonry(grid);

	let resizeTimer: ReturnType<typeof setTimeout>;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => layoutMasonry(grid), 100);
	});

	if (!filter) return;

	initFilter(filter, searchInput, grid);

	if (searchInput) {
		initSearch(searchInput, filter, grid);
	}
}
